import * as React from "react";
import { SortHeader } from "../Components/SortHeader";
import { Table, Column } from "../Components/QueryTable";
import { WebPortalClient } from "webportalclient";
import { ODataParams, useQuery, Query } from "../Components/Query";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { DragDrop } from "../misc/DragDrop";
import { FileRejection } from "react-dropzone";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import { Authorization } from "../misc/components/Authorization";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { useSecurityContext } from "../Core/SecurityContext";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";
import { FileDownload } from "../misc/FileDownload";

export const SupportDocumentsTable = () => {
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const { data, refresh } = useQuery();

    const onFileDelete = async (id) => {
        await client.client.deleteFile(id);
        refresh();
    }

    const onDownloadFile = async (fileId: string) => {
        const fileRecord = await client.client.getFile(fileId);
        const fileResult = await legacyClient.downloadFile(fileId);

        FileDownload(fileResult, fileRecord.fileName);
    }

    const menuFormatter = (value) => {
        if (value !== null && value !== undefined)
            return (
                <div className="text-center">
                    <div className="btn-group" role="group">
                        <button type="button" className="btn btn-primary btn-xs dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            <i className="fas fa-bars" aria-hidden="true" />
                        </button>
                        <ul className="dropdown-menu pull-right scrollable-menu" role="menu">
                            <li>
                                <a onClick={() => onDownloadFile(value.id)}>View</a>
                            </li>
                            <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.DeleteFile}>
                                <li>
                                    <a onClick={() => onFileDelete(value.id)}>Delete</a>
                                </li>
                            </Authorization>
                        </ul>
                    </div>
                </div>
            );
    }

    return (
        <Table automationId="documentsTable" data={data}>
            <Column field="fileName" header={<SortHeader title="File Name" field="fileName" />} />
            <Column field="" formatter={menuFormatter} />
        </Table>
    );
}

export const SupportDocumentsCore = ({ client, study }) => {
    const [alert, setAlert] = React.useState<string | undefined>();
    const { refresh } = useQuery();
    const { currentUserPermissions } = useSecurityContext();
    const [isUploading, setIsUploading] = React.useState(false);
    
    const uploadFile = async (content: File) => {
        const arrayBuffer = await content.arrayBuffer();
        let binaryString = '';
        const bytes = new Uint8Array(arrayBuffer);
        const length = bytes.byteLength;
        for (let i = 0; i < length; i++) {
            binaryString += String.fromCharCode(bytes[i]);
        }

        return await client.client.postFile({
            studyIds: [study.id],
            fileName: content.name,
            contentType: content.type,
            fileSizeInBytes: content.size,
            fileContent: btoa(binaryString),
        });
    }

    const onDrop = async (accepted: File[], rejected: FileRejection[]) => {
        setIsUploading(true);
        setAlert(undefined);


        if (rejected.length > 0) {
            setAlert(
                `${rejected[0].file.name} - ${rejected[0].errors.map(e => e.message).join(", ")}`
            );
            setIsUploading(false);
            return;
        }

        for (const file of accepted) {
            await uploadFile(file);
        }

        refresh();
        setIsUploading(false);
    };

    return (
        <>
            {alert ? <Alert show={true} category={AlertCategoryEnum.Danger}>{alert}</Alert> : null}
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Support Documents</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div className="row">
                                <div className="col-md-12">
                                    <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.AddFile}>
                                        <DragDrop
                                            id="DocumentsDragDrop"
                                            options={{
                                                onDrop,
                                                maxSize: 50000000,  // 50MB
                                                accept: {
                                                    "application/pdf": [".pdf"],
                                                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [], // .docx
                                                    "application/vnd.openxmlformats-officedocument.presentationml.presentation": [], // .pptx
                                                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [], // .xlsx
                                                    "application/msword": [".doc"],
                                                    "application/vnd.ms-powerpoint": [".ppt"],
                                                    "application/vnd.ms-excel": [".xls"],
                                                    "application/rtf": [".rtf"],
                                                    "video/mp4": [".mp4"],
                                                    "text/csv": [".csv"],
                                                    "text/plain": [".txt"],
                                                }
                                            }}
                                            isUploading={isUploading}
                                        />
                                    </Authorization>
                                </div>
                            </div>
                            <SupportDocumentsTable />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export const SupportDocuments = () => {
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();

    const handleRefresh = async (params: ODataParams) => {
        let filter = params.filter ? `(${params.filter}) and ` : '';

        const ignoreFileType = "text/xml";
        filter += `contentType ne '${ignoreFileType}' and studies/any(study: study/studyId eq ${study.id})`;

        return await client.client.getFiles(undefined, filter, params.orderby, undefined, params.top, params.skip, undefined, true);
    }

    return (
        <Query onRefresh={handleRefresh} initialOrdering={[{ field: "fileName", condition: "asc" }]}>
            <SupportDocumentsCore client={client} study={study} />
        </Query>
    );
}
